import { useState, useEffect, useCallback } from 'react'
import { Users, Plus, Phone, MapPin, Vote, ChevronRight, X, Check, Search } from 'lucide-react'
import { apiUrl } from '../config/api'
import PullToRefresh from '../components/ui/PullToRefresh'
import Button from '../components/ui/Button'

interface Contact {
  id: number; name: string; phone: string; cpf: string | null
  city_name: string | null; neighborhood: string | null
  electoral_zone: string | null; electoral_section: string | null
  created_at: string
}

type FormStep = 1 | 2 | 3

const stepLabels = {
  1: { title: 'Dados básicos', desc: 'Nome, telefone e cidade' },
  2: { title: 'Endereço', desc: 'Endereço completo do contato' },
  3: { title: 'Dados eleitorais', desc: 'Seção e zona eleitoral' },
}

export default function ContactsPage() {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [step, setStep] = useState<FormStep>(1)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [cities, setCities] = useState<{ id: number; name: string }[]>([])

  const [zones, setZones] = useState<{ id: number; nr_zona: number; nm_municipio: string }[]>([])
  const [sections, setSections] = useState<{ id: number; nr_secao: number }[]>([])
  const [form, setForm] = useState({
    name: '', phone: '', cpf: '', geo_city_id: '',
    city_name: '',
    zip_code: '', street: '', number: '', neighborhood: '', complement: '',
    tse_zone_id: '', tse_section_id: '', voter_registration: '',
  })

  const [stateId, setStateId] = useState<number | null>(null)

  // Busca state_id da org via home
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return
    fetch(apiUrl('/v1/community/my/home'), {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data?.geo_state_id) setStateId(d.data.geo_state_id) })
      .catch(() => {})
  }, [])

  // Carrega cidades quando tem stateId
  useEffect(() => {
    if (!stateId) return
    fetch(apiUrl(`/v1/geo/states/${stateId}/cities`))
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data) setCities(d.data) })
      .catch(() => {})
  }, [stateId])

  // Carrega zonas quando cidade é selecionada
  useEffect(() => {
    if (!form.geo_city_id) { setZones([]); return }
    const token = localStorage.getItem('token')
    fetch(apiUrl(`/v1/elections/zones?city_id=${form.geo_city_id}`), {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data) setZones(d.data) })
      .catch(() => {})
  }, [form.geo_city_id])

  // Carrega seções quando zona é selecionada
  useEffect(() => {
    if (!form.tse_zone_id) { setSections([]); return }
    const token = localStorage.getItem('token')
    fetch(apiUrl(`/v1/elections/zones/${form.tse_zone_id}/sections`), {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data) setSections(d.data) })
      .catch(() => {})
  }, [form.tse_zone_id])

  const fetchContacts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl('/v1/community/my/contacts'), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      setContacts(data.data || [])
    } catch {} finally { if (!silent) setLoading(false) }
  }, [])

  useEffect(() => { fetchContacts() }, [fetchContacts])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') fetchContacts(true)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchContacts])

  const handleRefresh = async () => { await fetchContacts() }

  function openForm() {
    setForm({ name: '', phone: '', cpf: '', geo_city_id: '', city_name: '', zip_code: '', street: '', number: '', neighborhood: '', complement: '', tse_zone_id: '', tse_section_id: '', voter_registration: '' })
    setStep(1)
    setSaveError('')
    setZones([])
    setSections([])
    setShowForm(true)
  }

  const [saveError, setSaveError] = useState('')
  const [cepLoading, setCepLoading] = useState(false)

  async function handleCepLookup() {
    const cep = form.zip_code.replace(/\D/g, '')
    if (cep.length !== 8) return
    setCepLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl(`/v1/geo/cep/${cep}`), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      if (res.ok) {
        const data = await res.json()
        const d = data.data
        if (d) {
          setForm(f => ({
            ...f,
            street: d.street || f.street,
            neighborhood: d.neighborhood || f.neighborhood,
            city_name: d.city || f.city_name,
            geo_city_id: d.geo_city_id ? String(d.geo_city_id) : f.geo_city_id,
          }))
        }
      }
    } catch {} finally { setCepLoading(false) }
  }

  async function handleSave() {
    setSaving(true)
    setSaveError('')
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl('/v1/voters'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone.replace(/\D/g, ''),
          cpf: form.cpf.replace(/\D/g, '') || undefined,
          geo_city_id: form.geo_city_id ? Number(form.geo_city_id) : undefined,
          zip_code: form.zip_code.replace(/\D/g, '') || undefined,
          street: form.street || undefined,
          number: form.number || undefined,
          neighborhood: form.neighborhood || undefined,
          complement: form.complement || undefined,
          tse_zone_id: form.tse_zone_id ? Number(form.tse_zone_id) : undefined,
          tse_section_id: form.tse_section_id ? Number(form.tse_section_id) : undefined,
          voter_registration: form.voter_registration || undefined,
          is_voter: true,
          is_contact: true,
          add_to_campaign: true,
        }),
      })
      if (res.ok) {
        setShowForm(false)
        setSaveError('')
        fetchContacts()
      } else {
        const err = await res.json()
        setSaveError(err.message || 'Erro ao salvar')
      }
    } catch { setSaveError('Erro de conexão') } finally { setSaving(false) }
  }

  function formatPhone(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 2) return d
    if (d.length <= 7) return `(${d.slice(0,2)}) ${d.slice(2)}`
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`
  }

  function formatCpf(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 3) return d
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`
  }

  const filtered = search
    ? contacts.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search.replace(/\D/g, '')))
    : contacts

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="h-12 px-4 items-center border-b border-border-subtle hidden lg:flex">
        <Users size={20} className="text-content-muted mr-2" />
        <h1 className="text-base font-semibold text-content-normal">Contatos</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto w-full">

        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-bold text-content-normal">Meus contatos</h2>
          <p className="text-xs text-content-muted">Sua rede de apoiadores</p>
        </div>

        {/* Busca */}
        {contacts.length > 3 && (
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nome ou telefone..."
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
            />
          </div>
        )}

        {/* Explicação das fases */}
        {contacts.length === 0 && !loading && (
          <div className="mb-6">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-content-normal mb-3">Monte sua rede de apoiadores!</h3>
              <p className="text-xs text-content-muted mb-4">Cadastre seus contatos em 3 etapas simples:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0">1</div>
                  <div>
                    <p className="text-xs font-semibold text-content-normal">Nome, telefone e cidade</p>
                    <p className="text-[10px] text-content-muted">O básico pra identificar o contato</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/60 text-white flex items-center justify-center text-xs font-bold shrink-0">2</div>
                  <div>
                    <p className="text-xs font-semibold text-content-normal">Endereço completo</p>
                    <p className="text-[10px] text-content-muted">Pra mapear a rede por bairro</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/30 text-white flex items-center justify-center text-xs font-bold shrink-0">3</div>
                  <div>
                    <p className="text-xs font-semibold text-content-normal">Seção e zona eleitoral</p>
                    <p className="text-[10px] text-content-muted">Pra análise de votos por seção</p>
                  </div>
                </div>
              </div>

              <Button onClick={openForm} fullWidth className="mt-4" icon={<Plus size={16} />}>
                Adicionar primeiro contato
              </Button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        )}

        {/* Lista */}
        {!loading && filtered.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-content-muted mb-2">{contacts.length} contato{contacts.length !== 1 ? 's' : ''}</p>
            {filtered.map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-border-subtle">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-content-normal truncate">{c.name}</p>
                  <div className="flex items-center gap-2 text-[11px] text-content-muted">
                    <span className="flex items-center gap-0.5"><Phone size={10} />{formatPhone(c.phone)}</span>
                    {c.city_name && <span className="flex items-center gap-0.5"><MapPin size={10} />{c.city_name}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {c.neighborhood && <div className="w-2 h-2 rounded-full bg-primary/60" title="Endereço" />}
                  {c.electoral_section && <div className="w-2 h-2 rounded-full bg-primary" title="Dados eleitorais" />}
                  {!c.neighborhood && !c.electoral_section && <div className="w-2 h-2 rounded-full bg-primary/20" title="Só dados básicos" />}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && contacts.length > 0 && filtered.length === 0 && (
          <p className="text-sm text-content-muted text-center py-8">Nenhum contato encontrado</p>
        )}
      </div>

      {/* Botão flutuante adicionar */}
      {!showForm && (
        <button
          onClick={openForm}
          className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center z-20"
        >
          <Plus size={24} />
        </button>
      )}

      {/* Modal de cadastro */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50 lg:cursor-pointer animate-[fadeIn_0.2s_ease-out]" onClick={() => { if (window.matchMedia('(min-width: 1024px)').matches) setShowForm(false) }} />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-surface-primary rounded-t-2xl max-h-[90vh] overflow-y-auto safe-area-inset-bottom animate-[slideUp_0.3s_ease-out] lg:inset-0 lg:m-auto lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2 lg:max-w-lg lg:rounded-2xl lg:shadow-2xl lg:max-h-[85vh] lg:animate-[scaleIn_0.2s_ease-out]">
            <div className="sticky top-0 bg-surface-primary border-b border-border-subtle px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-base font-bold text-content-normal">Novo contato</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full bg-surface-secondary flex items-center justify-center">
                <X size={18} className="text-content-muted" />
              </button>
            </div>

            {/* Steps indicator */}
            <div className="px-4 py-3 flex items-center gap-2">
              {([1,2,3] as FormStep[]).map(s => (
                <button
                  key={s}
                  onClick={() => setStep(s)}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-surface-active'}`}
                />
              ))}
            </div>
            <div className="px-4 pb-2">
              <p className="text-sm font-bold text-content-normal">{stepLabels[step].title}</p>
              <p className="text-[11px] text-content-muted">{stepLabels[step].desc}</p>
            </div>

            <div className="px-4 pb-6 space-y-4">

              {/* FASE 1 */}
              {step === 1 && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Nome completo *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Ex: Maria da Silva"
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Telefone (WhatsApp) *</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={formatPhone(form.phone)}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="(82) 99999-9999"
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">CPF</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatCpf(form.cpf)}
                      onChange={e => setForm(f => ({ ...f, cpf: e.target.value }))}
                      placeholder="000.000.000-00"
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Cidade</label>
                    <select
                      value={form.geo_city_id}
                      onChange={e => {
                        const city = cities.find(c => c.id === Number(e.target.value))
                        setForm(f => ({ ...f, geo_city_id: e.target.value, city_name: city?.name || '' }))
                      }}
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal appearance-none"
                    >
                      <option value="">Selecione a cidade</option>
                      {cities.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {saveError && <p className="text-xs text-status-danger text-center">{saveError}</p>}
                  <div className="flex gap-3 pt-2">
                    <Button fullWidth onClick={handleSave} loading={saving} disabled={!form.name.trim() || form.phone.replace(/\D/g, '').length < 10}>
                      Salvar contato
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setStep(2)} disabled={!form.name.trim() || form.phone.replace(/\D/g, '').length < 10}>
                      Próximo <ChevronRight size={14} />
                    </Button>
                  </div>
                </>
              )}

              {/* FASE 2 */}
              {step === 2 && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">CEP</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.zip_code.length > 5 ? `${form.zip_code.slice(0,5)}-${form.zip_code.slice(5)}` : form.zip_code}
                        onChange={e => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 8)
                          setForm(f => ({ ...f, zip_code: v }))
                        }}
                        placeholder="57000-000"
                        className="flex-1 px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                      />
                      <Button
                        size="sm"
                        onClick={handleCepLookup}
                        loading={cepLoading}
                        disabled={form.zip_code.replace(/\D/g, '').length !== 8}
                      >
                        Buscar
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Rua</label>
                    <input
                      type="text"
                      value={form.street}
                      onChange={e => setForm(f => ({ ...f, street: e.target.value }))}
                      placeholder="Rua / Avenida"
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-content-muted block mb-1">Número</label>
                      <input
                        type="text"
                        value={form.number}
                        onChange={e => setForm(f => ({ ...f, number: e.target.value }))}
                        placeholder="123"
                        className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-content-muted block mb-1">Bairro</label>
                      <input
                        type="text"
                        value={form.neighborhood}
                        onChange={e => setForm(f => ({ ...f, neighborhood: e.target.value }))}
                        placeholder="Bairro"
                        className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Complemento</label>
                    <input
                      type="text"
                      value={form.complement}
                      onChange={e => setForm(f => ({ ...f, complement: e.target.value }))}
                      placeholder="Apto, bloco..."
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button fullWidth variant="secondary" onClick={() => setStep(1)}>
                      Voltar
                    </Button>
                    <Button fullWidth onClick={handleSave} loading={saving}>
                      Salvar
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setStep(3)} disabled={!form.street.trim() || !form.neighborhood.trim()}>
                      Próximo <ChevronRight size={14} />
                    </Button>
                  </div>
                </>
              )}

              {/* FASE 3 */}
              {step === 3 && (
                <>
                  <div className="bg-surface-secondary rounded-xl p-4 mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Vote size={16} className="text-primary" />
                      <p className="text-xs font-bold text-content-normal">Local de votação</p>
                    </div>
                    <p className="text-[11px] text-content-muted">
                      Selecione a zona e seção eleitoral. O eleitor encontra no título de eleitor ou no app e-Título.
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Zona eleitoral</label>
                    <select
                      value={form.tse_zone_id}
                      onChange={e => setForm(f => ({ ...f, tse_zone_id: e.target.value, tse_section_id: '' }))}
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal appearance-none"
                    >
                      <option value="">Selecione a zona</option>
                      {zones.map(z => (
                        <option key={z.id} value={z.id}>Zona {z.nr_zona} — {z.nm_municipio}</option>
                      ))}
                    </select>
                    {!form.geo_city_id && <p className="text-[10px] text-content-muted mt-1">Selecione a cidade primeiro (etapa 1)</p>}
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Seção eleitoral</label>
                    <select
                      value={form.tse_section_id}
                      onChange={e => setForm(f => ({ ...f, tse_section_id: e.target.value }))}
                      disabled={!form.tse_zone_id}
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal appearance-none disabled:opacity-50"
                    >
                      <option value="">Selecione a seção</option>
                      {sections.map(s => (
                        <option key={s.id} value={s.id}>Seção {s.nr_secao}</option>
                      ))}
                    </select>
                    {!form.tse_zone_id && <p className="text-[10px] text-content-muted mt-1">Selecione a zona primeiro</p>}
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-content-muted block mb-1">Título de eleitor</label>
                    <input
                      type="text"
                      value={form.voter_registration}
                      onChange={e => setForm(f => ({ ...f, voter_registration: e.target.value }))}
                      placeholder="Número do título (opcional)"
                      className="w-full px-4 py-3 text-sm bg-surface-secondary rounded-xl border border-border-subtle focus:border-primary focus:outline-none text-content-normal placeholder:text-content-muted"
                    />
                  </div>

                  {saveError && <p className="text-xs text-status-danger text-center">{saveError}</p>}
                  <div className="flex gap-3 pt-2">
                    <Button fullWidth variant="secondary" onClick={() => setStep(2)}>
                      Voltar
                    </Button>
                    <Button fullWidth loading={saving} onClick={handleSave}>
                      Salvar contato
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </PullToRefresh>
  )
}
